"use client";

import { useRouter } from "next/navigation";
import type { FormEvent, ReactNode } from "react";

/**
 * Client wrapper around the board's GET search form. Kept deliberately thin so
 * it pulls in NO server-only code (importing the filters/DB layer here would
 * drag `postgres` into the client bundle). It only intercepts the submit.
 *
 * Without JS the native GET submit still works (shareable URL). With JS we
 * navigate client-side instead — a full reload would remount <SearchTabs>, which
 * loses the active tab and, for signed-in users, fails to persist the new search
 * to their account. Client nav keeps the tab state live so the active tab adopts
 * the search and saves itself. Reads values straight off the form element, so
 * the server-rendered hidden filter inputs are carried along automatically.
 */
export function ClientSearchForm({
  action,
  className,
  children,
}: {
  action: string;
  className?: string;
  children: ReactNode;
}) {
  const router = useRouter();

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const params = new URLSearchParams();
    for (const [name, value] of new FormData(e.currentTarget).entries()) {
      const v = String(value).trim();
      if (v) params.append(name, v);
    }
    const qs = params.toString();
    router.push(qs ? `${action}?${qs}` : action, { scroll: false });
  }

  return (
    <form
      method="get"
      action={action}
      role="search"
      className={className}
      onSubmit={onSubmit}
    >
      {children}
    </form>
  );
}
